import { Component, inject, Input, OnInit, signal } from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { Gender, PatientLogin, UserService } from "@main/users";
import { FormButtonsConfig } from "@mipro/components";
import { PlayerService } from "./player.service";
import { ActivatedRoute, Router } from "@angular/router";
import { MessageService } from "primeng/api";
import { Message } from "@bbwt/classes";
import { NamingConventionService } from "@mipro/services/naming-convention.service";
import { UserPatientValidators } from "@mipro/custom-controls/user-patient/validators";
import { firstValueFrom, map } from "rxjs";
import { DuplicatePatientCheckResult } from "./duplicate-patient";
import { NhsFieldsModifiedPromptService } from "@mipro/services/nhs-fields-modified-prompt.service";
import { CreateSelfRegisterResult, ResultErrorType, ResultType } from "./create-self-register-result";
import { DateOfBirthMismatchNormalized, LastNameMismatch } from "@mipro/classes/nhs-mipro-data-mismatch";
import { FormEventType } from "@main/formio/components/form-events/event-type";
import { FormEventsService } from "@main/formio/services/formEvents.service";
import { FormEventPayload, FormEventsDTO } from "@main/formio/dto/formEventsDTO";
import { UserHelperService } from "@main/users/user-helper.service";
import { OrganizationService } from "@main/companies";
import { AppStorage } from "@bbwt/utils/app-storage";
import { OrganizationSelfRegisterInfo } from "@main/companies/organization";
import { SelfRegisterInfoService } from "@project/self-registration/self-register-info.service";
import { removePathParam } from "@bbwt/utils/routes";
import { SlotDataService } from "@project/appointments/components/diary-calendar/components/slot/slot-data.service";
import { SlotData } from "@project/appointments/components/diary-calendar/components/slot/slot-data";

declare type SelfRegistrationQueryParams = {
    verificationNhsCode?: string;
    error?: string;
    error_description?: string;
    name?: string;
    surname?: string;
    dateOfBirth?: Date;
    email?: string;
    origin?: string;
    nhsNumber?: string;
    externalpatientid?: string;
};

@Component({
    selector: "create-self-register-player-form",
    templateUrl: "./create-self-register-player-form.component.html",
    styleUrls: ["./create-self-register-player-form.component.scss"],
    providers: [NhsFieldsModifiedPromptService, SelfRegisterInfoService]
})
export class CreateSelfRegisterPlayerFormComponent implements OnInit {
    protected readonly slfRegisterInfoService = inject(SelfRegisterInfoService);
    private readonly slotDataService = inject(SlotDataService);

    protected noSelfRegUrlAvailable = true;
    protected verificationNhsCode: string;
    protected playerForm = this.fb.group({
        title: [<string>null, UserPatientValidators.title],
        firstName: [<string>null, UserPatientValidators.firstName],
        lastName: [<string>null, UserPatientValidators.lastName],
        dateOfBirth: [<Date>null, UserPatientValidators.dateOfBirth],
        gender: [<Gender>null, UserPatientValidators.gender],
        phoneNumber: [<string>null, UserPatientValidators.phoneNumber],
        email: [<string>null, UserPatientValidators.email(true)],
        addressLine1: [<string>null, UserPatientValidators.addressLine(true)],
        addressLine2: [<string>null, UserPatientValidators.addressLine(false)],
        addressLine3: [<string>null, UserPatientValidators.addressLine(false)],
        addressLine4: [<string>null, UserPatientValidators.addressLine(false)],
        postcode: [<string>null, UserPatientValidators.postcode(true)],
        participationReason: [<string>null, UserPatientValidators.participationReason],
        consentToUpdate: [false],
        nhsNumber: [{ value: <string>null, disabled: true }],
        selfRegister: [true]
    });
    protected orgSelfRegistrationURL: string;
    protected lastNameMistmatch: LastNameMismatch;
    protected dobMistmatch: DateOfBirthMismatchNormalized;
    protected readonly linkNhsMiproVisible = signal(false);

    savingPlayer: boolean = false;
    buttonsConfig = <FormButtonsConfig>{
        saveLabel: "Register",
        cancelFunc: () => this.cancelFunc()
    };

    @Input() formStyle: any;
    @Input() url: string;
    @Input() formTitle: string = "Personal Info";
    @Input() draftAppointmentKey: string;

    showSuccessfulRegistrationForm = false;
    timerSeconds = 10;
    timerId: any;

    nhsLoginDialogVisible = false;
    nhsActionInProgress = false;
    nhsActionMessage = "";
    nhsActionSuccessful = false;
    protected nhsConsentNotGiven = false;

    duplicatesDialogVisible = false;
    duplicatePatients: DuplicatePatientCheckResult;
    protected origin: string;

    formEventType = FormEventType.SelfRegisterPatient;
    isAutoApproveSelfRegisteredPatients = false;
    //hasFormEvent = false;
    init = false;
    formEvent: FormEventsDTO = null;
    showNHSDialog: boolean = false;
    selfRegInfo: OrganizationSelfRegisterInfo;
    externalPatientId: string;

    constructor(
        private fb: FormBuilder,
        private playerService: PlayerService,
        private router: Router,
        private messageService: MessageService,
        public namingConvService: NamingConventionService,
        private route: ActivatedRoute,
        private formEventsService: FormEventsService,
        private userHelperService: UserHelperService,
        private userService: UserService,
        private organizationService: OrganizationService
    ) {}

    async ngOnInit() {
        this.orgSelfRegistrationURL = await firstValueFrom(this.route.params.pipe(map(params => <string>params.organizationurl)));
        this.noSelfRegUrlAvailable = !this.orgSelfRegistrationURL;

        const queryParams = (await firstValueFrom(
            this.route.queryParams.pipe(map(p => ({ ...p, dateOfBirth: new Date(p.dateOfBirth) })))
        )) as SelfRegistrationQueryParams;
        this.verificationNhsCode = queryParams.verificationNhsCode;
        this.origin = queryParams.origin;
        this.externalPatientId = queryParams.externalpatientid;

        if (this.orgSelfRegistrationURL) {
            this.selfRegInfo = await this.organizationService.isSelfRegistrationEnabled(this.orgSelfRegistrationURL);
        }

        this.formEvent = await this.formEventsService.getCurrentFormEvent(this.formEventType, this.orgSelfRegistrationURL);

        await this.setAutoApprove();
        this.setNhsError(queryParams.error, queryParams.error_description);
        this.setInitialValues(queryParams);
        this.init = true;
    }

    get email() {
        return this.playerForm?.get("email")?.value;
    }

    get phoneNumber() {
        return this.playerForm?.get("phoneNumber")?.value;
    }

    get addressLine1() {
        return this.playerForm?.get("addressLine1")?.value;
    }

    get addressLine2() {
        return this.playerForm?.get("addressLine2")?.value;
    }

    get postcode() {
        return this.playerForm?.get("postcode")?.value;
    }

    get nhsNumber() {
        return this.playerForm?.get("nhsNumber")?.value;
    }

    get invalidEmailAddressPhoneNumber(): boolean {
        return !((this.addressLine1 && this.addressLine2 && this.postcode) || this.email || this.phoneNumber);
    }

    get dateOfBirth() {
        return this.playerForm?.get("dateOfBirth")?.getRawValue();
    }

    get surname() {
        return this.playerForm?.get("lastName")?.getRawValue();
    }

    async createPlayer(skipDuplicatesCheck?: boolean) {
        if (this.playerForm.invalid || this.savingPlayer) {
            this.playerForm.markAllAsTouched();
            return;
        }

        this.savingPlayer = true;
        const player: PatientLogin = { 
            ...this.playerForm.getRawValue(), 
            externalId: this.externalPatientId,
            fromBookingAppointment: this.url.includes("book-appointment")
        } as any;

        const createResult = await this.playerService.createSelfRegisterPlayer(
            player,
            this.orgSelfRegistrationURL,
            skipDuplicatesCheck,
            this.verificationNhsCode
        );
        this.slfRegisterInfoService.clearExternalId();
        this.externalPatientId = null;
        this.url = removePathParam(this.url, "externalpatientid");

        let bookingError = false;
        if (createResult.success) {
            if(this.url.includes("book-appointment")) {
                const draftAppointmentData = AppStorage.getItem<SlotData>(this.draftAppointmentKey);
                if(draftAppointmentData) {
                    try {
                        await this.slotDataService.bookAppointment(
                            draftAppointmentData.id, 
                            draftAppointmentData.notes, 
                            null, 
                            createResult.patientId, 
                            createResult.resultType !== ResultType.LinkedToOrganization);
                        AppStorage.setItem(this.draftAppointmentKey, null);
                    } catch (error) {
                        bookingError = true;
                        await this.playerService.resendInvitationByPatientId(createResult.patientId);
                    }
                }
            }

            await this.handleSuccessfulCreateResult(createResult, bookingError);
        } else {
            await this.handleFailedCreateResult(createResult, bookingError);
        }

        this.savingPlayer = false;
    }

    private async handleSuccessfulCreateResult(createResult: CreateSelfRegisterResult, bookingError?: boolean) {
        if(createResult.errorType === ResultErrorType.LinkIM1Failed) {
            this.messageService.add(Message.Warning(
                "There was an issue connecting to your GP record. Please try again. If the problem persists, please refer to the Relevant Frequently Asked Questions (FAQ) section"
            ));
        }

        if (createResult.resultType === ResultType.LoggedIn) {
            if (this.userService.isLogged && this.shouldTriggerFormEvent(createResult.patientId)) {
                this.triggerSelfRegistrationEvent(createResult.patientId);
                return;
            } else {
                AppStorage.setItem(AppStorage.LoginInProcess, true);
                const returnUrl = this.slfRegisterInfoService.goToUrl ?? AppStorage.getItemFromSession("self-registration-returnUrl");

                setTimeout(() => {
                    window.location.href = returnUrl ?? "/";
                }, 3000);
            }
        } else if (createResult.resultType === ResultType.LinkedToOrganization) {
            this.messageService.add(Message.Success("We've successfully linked your account to the Organisation.", "Register Patient"));
            if (this.userService.isLogged && this.shouldTriggerFormEvent(createResult.patientId)) {
                this.triggerSelfRegistrationEvent(createResult.patientId);
                return;
            } else {
                const returnUrl = this.slfRegisterInfoService.goToUrl ?? AppStorage.getItemFromSession("self-registration-returnUrl");
                if(!this.url.includes("book-appointment")) {
                    this.router.navigate(["/account/login"], { queryParams: returnUrl ? { returnUrl } : {} });
                } else {
                    this.router.navigate(["../../confirm-booking"], {
                        queryParams: {
                            clinic: this.slfRegisterInfoService.clinic,
                            externalpatientid: await firstValueFrom(this.slfRegisterInfoService.externalPatientId$),
                            goToUrl: this.url,
                            bookingError: bookingError,
                            newPatient: createResult.resultType !== ResultType.LinkedToOrganization
                        }, relativeTo: this.route
                    });
                }
            }
        } else {
            this.checkCreateSelfRegisterResult(createResult, bookingError);
        }
    }

    private async handleFailedCreateResult(createResult: CreateSelfRegisterResult, bookingError?: boolean) {
        if (createResult.errorType === ResultErrorType.Validation) {
            this.messageService.add(Message.Error(createResult.errorMessage, "Register Patient"));
        } else if (createResult.errorType === ResultErrorType.Im1) {
            await this.showSuccessfulRegistrationFormF(bookingError);
        } else if (createResult.errorType === ResultErrorType.LinkNhsMipro) {
            this.linkNhsMiproVisible.set(true);
        } else if (createResult.errorType === ResultErrorType.IsUser) {
            this.showNHSDialog = true;
        } else if(createResult.errorType === ResultErrorType.LinkIM1Failed){
            this.messageService.add(Message.Warning(
                "There was an issue connecting to your GP record. Please try again. If the problem persists, please refer to the Relevant Frequently Asked Questions (FAQ) section"
            ));
        }
    }

    private async checkCreateSelfRegisterResult(createResult: CreateSelfRegisterResult, bookingError?: boolean) {
        if (createResult.resultType === ResultType.Duplicates) {
            this.nhsLoginDialogVisible = false;
            this.nhsActionInProgress = false;
            this.duplicatePatients = createResult.duplicates;
            this.duplicatesDialogVisible = true;

            await this.router.navigateByUrl(window.location.pathname);
        } else {
            this.nhsActionInProgress = false;
            this.nhsActionSuccessful = true;
            this.duplicatesDialogVisible = false;
            this.duplicatePatients = <DuplicatePatientCheckResult>{};

            if (this.shouldTriggerFormEvent(createResult.patientId)) {
                this.triggerSelfRegistrationEvent(createResult.patientId);
                return;
            }

            if (this.url) {
                await this.showSuccessfulRegistrationFormF(bookingError, createResult.resultType !== ResultType.LinkedToOrganization);
            }
        }
    }

    private shouldTriggerFormEvent(patientId: string) {
        return this.isAutoApproveSelfRegisteredPatients && this.formEvent && !!patientId;
    }

    private triggerSelfRegistrationEvent(patientId: string) {
        this.formEventsService.triggerFormEvent(<FormEventPayload>{
            formEventType: this.formEventType,
            patientId: patientId,
            selfRegisterOrganizationUrl: this.orgSelfRegistrationURL,
            selfRegisterOrganizationId: this.userHelperService.hashedOrganizationId
        });
    }

    protected clearForm() {
        Object.keys(this.playerForm.controls).forEach(key => {
            const control = this.playerForm.get(key);
            if (control && !control.disabled) {
                control.reset();
            }
        });
    }

    protected cancelFunc() {
        if (this.url) {
            this.router.navigateByUrl(this.url);
        }
    }

    private async showSuccessfulRegistrationFormF(bookingError?: boolean, newPatient?: boolean) {
        this.showSuccessfulRegistrationForm = true;
        const navigateToUrl = !this.url.includes("book-appointment") ? "confirmation" : "confirm-booking";

        this.router.navigate([`../../${navigateToUrl}`], {
            queryParams: {
                clinic: this.slfRegisterInfoService.clinic,
                externalpatientid: await firstValueFrom(this.slfRegisterInfoService.externalPatientId$),
                goToUrl: this.url,
                bookingError: bookingError,
                newPatient: newPatient
            }, relativeTo: this.route
        });
    }

    protected closeNhsRegistrationDialog() {
        if (this.nhsActionMessage) {
            this.router.navigate([window.location.pathname], { replaceUrl: true });
        }
        this.nhsLoginDialogVisible = false;
    }

    private setNhsError(error: string, error_description: string) {
        if (error) {
            this.nhsConsentNotGiven = error_description === "ConsentNotGiven";
            const message = this.nhsConsentNotGiven
                ? `
                    <p>
                        We are unable to register you as a ORB Patient as you have decided not to allow ORB
                        to access your NHS login data.
                    </p>
                    <p>
                        To Register with ORB you should enter your Password and press Complete Registration.
                    </p>`
                : `Error occured: ${error}`;
            this.nhsLoginDialogVisible = true;
            this.nhsActionMessage = message;
        }
    }

    private setInitialValues(queryParams: SelfRegistrationQueryParams) {
        if (!queryParams.name || !queryParams.surname || !queryParams.dateOfBirth || !queryParams.email || !queryParams.origin) {
            return;
        }

        this.playerForm.patchValue({
            firstName: queryParams.name,
            lastName: queryParams.surname,
            dateOfBirth: queryParams.dateOfBirth,
            email: queryParams.email
        });

        this.playerForm.controls.firstName.disable();
        this.playerForm.controls.lastName.disable();
        this.playerForm.controls.dateOfBirth.disable();
    }

    private async setAutoApprove() {
        if (this.noSelfRegUrlAvailable) {
            this.isAutoApproveSelfRegisteredPatients = await this.organizationService.isAutoApproveSelfRegisteredPatientsEnabledByOrgId(
                this.userHelperService.hashedOrganizationId
            );
        } else {
            this.isAutoApproveSelfRegisteredPatients = await this.organizationService.isAutoApproveSelfRegisteredPatientsEnabledByOrgUrl(
                this.orgSelfRegistrationURL
            );
        }
    }

    onCancel() {
        this.showNHSDialog = false;
    }
}
